import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams, useLocation } from 'react-router-dom';
import { holidayMasterApi } from '../api/holidayMasterApi';
import { appToast } from '../../../../components/AppToast';

const EMPTY_FORM = {
  name: '',
  date: '',
  branch_id: 0,
  group_id_csv: '',
};

export function useHolidayMasterEntry() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: routeId } = useParams();
  const [searchParams] = useSearchParams();
  const queryId = searchParams.get('id');
  const editId = routeId || queryId;
  const isEdit = Boolean(editId);

  const [form, setForm] = useState(EMPTY_FORM);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const isPayroll = location.pathname.startsWith('/payroll');
  const listPath = isPayroll ? '/payroll/holiday-master' : '/masters/holiday-master/list';

  // Load available active employee groups
  useEffect(() => {
    holidayMasterApi.getEmployeeGroups().then((groups) => {
      setAvailableGroups(groups);
    });
  }, []);

  // Load existing holiday for edit
  useEffect(() => {
    if (isEdit) {
      setIsLoading(true);
      holidayMasterApi
        .getById(Number(editId))
        .then((data) => {
          if (data) {
            setForm({
              name: data.name || '',
              date: data.date || data.hDate || '',
              branch_id: data.branch_id || 0,
              group_id_csv: data.group_id_csv || '',
            });
          }
        })
        .catch((err) => {
          console.error(err);
          appToast.error('Failed to load Holiday details.');
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [editId, isEdit]);

  const patchForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const selectedGroupIds = (form.group_id_csv || '')
    .split(',')
    .map((s) => Number(s.trim()))
    .filter((n) => !isNaN(n) && n > 0);

  const toggleGroup = (groupId) => {
    const numericId = Number(groupId);
    if (!numericId) return;
    setForm((prev) => {
      const current = (prev.group_id_csv || '')
        .split(',')
        .map((s) => Number(s.trim()))
        .filter((n) => !isNaN(n) && n > 0);
      const exists = current.includes(numericId);
      const updated = exists
        ? current.filter((g) => g !== numericId)
        : [...current, numericId];
      return { ...prev, group_id_csv: updated.join(',') };
    });
  };

  const toggleAllGroups = () => {
    setForm((prev) => {
      const allIds = availableGroups
        .map((g) => (typeof g === 'object' && g !== null ? Number(g.id) : Number(g)))
        .filter((n) => !isNaN(n) && n > 0);
      const current = (prev.group_id_csv || '')
        .split(',')
        .map((s) => Number(s.trim()))
        .filter((n) => !isNaN(n) && n > 0);
      const allSelected = allIds.length > 0 && allIds.every((id) => current.includes(id));
      return {
        ...prev,
        group_id_csv: allSelected ? '' : allIds.join(','),
      };
    });
  };

  const validate = () => {
    if (!form.name.trim()) {
      appToast.error('Holiday Name is required.');
      return false;
    }
    if (!form.date) {
      appToast.error('Holiday Date is required.');
      return false;
    }
    return true;
  };

  const handleSave = async ({ exitAfter = true } = {}) => {
    if (!validate()) return;
    setIsSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        date: form.date,
        branch_id: form.branch_id,
        group_id_csv: form.group_id_csv || '',
      };

      if (isEdit) {
        await holidayMasterApi.update(Number(editId), payload);
        appToast.success('Holiday updated successfully.');
      } else {
        await holidayMasterApi.create(payload);
        appToast.success('Holiday created successfully.');
      }

      if (exitAfter) {
        navigate(listPath);
      } else {
        setForm(EMPTY_FORM);
      }
    } catch (err) {
      console.error(err);
      appToast.error(err?.response?.data?.detail || 'Failed to save Holiday record.');
    } finally {
      setIsSaving(false);
    }
  };

  return {
    state: {
      form,
      selectedGroupIds,
      availableGroups,
      isLoading,
      isSaving,
      isEdit,
      listPath,
    },
    actions: {
      patchForm,
      toggleGroup,
      toggleAllGroups,
      handleSave,
    },
  };
}
